// Project state store
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { projectsApi, type Project } from '@/lib/api/client'

interface ProjectState {
  projects: Project[]
  currentProject: Project | null
  isLoading: boolean
  error: string | null

  // Actions - all require accountId
  loadProjects: (accountId: string) => Promise<void>
  selectProject: (accountId: string, projectId: string) => Promise<void>
  createProject: (accountId: string, name: string, slug: string, description?: string) => Promise<Project>
  updateProject: (accountId: string, projectId: string, updates: { name?: string; description?: string; visualAnalysisEnabled?: boolean }) => Promise<void>
  deleteProject: (accountId: string, projectId: string) => Promise<void>
  clearCurrentProject: () => void
  clearError: () => void
  clearProjects: () => void
}

export const useProjectStore = create<ProjectState>()(
  persist(
    (set, get) => ({
      projects: [],
      currentProject: null,
      isLoading: false,
      error: null,

      loadProjects: async (accountId: string) => {
        set({ isLoading: true, error: null })
        try {
          const projects = await projectsApi.list(accountId)
          set({ projects, isLoading: false })

          // If we have a currentProject, update it from the fresh list
          const current = get().currentProject
          if (current) {
            const updated = projects.find(p => p.id === current.id)
            if (updated) {
              set({ currentProject: updated })
            } else {
              // Project no longer accessible
              set({ currentProject: null })
            }
          }
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to load projects'
          set({ error: message, isLoading: false })
        }
      },

      selectProject: async (accountId: string, projectId: string) => {
        set({ isLoading: true, error: null })
        try {
          const project = await projectsApi.get(accountId, projectId)
          set({ currentProject: project, isLoading: false })
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to load project'
          set({ error: message, isLoading: false })
          throw err
        }
      },

      createProject: async (accountId: string, name: string, slug: string, description?: string) => {
        set({ isLoading: true, error: null })
        try {
          const project = await projectsApi.create(accountId, name, slug, description)
          set(state => ({
            projects: [...state.projects, project],
            currentProject: project,
            isLoading: false,
          }))
          return project
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to create project'
          set({ error: message, isLoading: false })
          throw err
        }
      },

      updateProject: async (accountId: string, projectId: string, updates: { name?: string; description?: string; visualAnalysisEnabled?: boolean }) => {
        set({ isLoading: true, error: null })
        try {
          const project = await projectsApi.update(accountId, projectId, updates)
          set(state => ({
            projects: state.projects.map(p => p.id === projectId ? project : p),
            currentProject: state.currentProject?.id === projectId ? project : state.currentProject,
            isLoading: false,
          }))
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to update project'
          set({ error: message, isLoading: false })
          throw err
        }
      },

      deleteProject: async (accountId: string, projectId: string) => {
        set({ isLoading: true, error: null })
        try {
          await projectsApi.delete(accountId, projectId)
          set(state => ({
            projects: state.projects.filter(p => p.id !== projectId),
            currentProject: state.currentProject?.id === projectId ? null : state.currentProject,
            isLoading: false,
          }))
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to delete project'
          set({ error: message, isLoading: false })
          throw err
        }
      },

      clearCurrentProject: () => set({ currentProject: null }),

      clearError: () => set({ error: null }),

      clearProjects: () => set({ projects: [], currentProject: null }),
    }),
    {
      name: 'ispot-project-v3',
      partialize: (state) => ({
        // Only persist current project selection
        currentProject: state.currentProject,
      }),
    }
  )
)
